import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { tmdbAPI } from '../services/api';
import MediaCard from '../components/MediaCard';
import { HiOutlineSearch, HiOutlineFilm, HiOutlineX } from 'react-icons/hi';

/**
 * Página de Busca otimizada para mobile
 * Estilo similar ao "Em alta esta semana" - cards em scroll horizontal
 */
function MobileSearch() {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);


  const handleSearch = useCallback(async () => {
    if (!query.trim()) {
      setResults([]);
      setSearched(false);
      return;
    }

    setLoading(true);
    setSearched(true);
    try {
      const response = await tmdbAPI.search(query);
      setResults(response.data.results || []);
    } catch (error) {
      console.error('Error searching:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      handleSearch();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [query, handleSearch]);

  const clearSearch = () => {
    setQuery('');
    setResults([]);
    setSearched(false);
  };

  const sectionTitle = searched
    ? `${t('search.resultsFor')} "${query}"`
    : t('search.mySearch');

  return (
    <div className="mobile-search">
      {/* Search Bar iOS Style */}
      <div className="mobile-search__bar">
        <div className="mobile-search__input-wrapper">
          <HiOutlineSearch className="mobile-search__icon" size={20} />
          <input
            type="text"
            className="mobile-search__input"
            placeholder={t('search.placeholder')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
          {query.length > 0 && (
            <button 
              className="mobile-search__clear"
              onClick={clearSearch}
              aria-label="Clear search"
            >
              <HiOutlineX size={18} />
            </button>
          )}
        </div>
      </div>

      {/* Seção de Resultados */}
      {loading ? (
        <section className="mobile-section">
          <div className="mobile-loading">
            <div className="spinner"></div>
          </div>
        </section>
      ) : searched ? (
        <section className="mobile-section">
          <div className="mobile-section__header">
            <HiOutlineSearch size={20} />
            <h2>{sectionTitle}</h2>
            {results.length > 0 && (
              <span className="mobile-section__count">({results.length})</span>
            )}
          </div>
          {results.length > 0 ? (
            <div className="mobile-scroll-list">
              {results.map((item) => (
                <div key={`${item.media_type || item.id}-${item.id}`} className="mobile-scroll-item">
                  <MediaCard item={item} compact />
                </div>
              ))}
            </div>
          ) : (
            <div className="mobile-empty">
              <div className="mobile-empty__icon">
                <HiOutlineFilm size={48} />
              </div>
              <h3>{t('search.noResults')}</h3>
              <p>{t('search.tryDifferentTerm')}</p>
            </div>
          )}
        </section>
      ) : (
        <section className="mobile-section">
          <div className="mobile-empty">
            <div className="mobile-empty__icon">
              <HiOutlineSearch size={48} />
            </div>
            <h3>{t('search.searchToFind', 'Busque filmes e séries')}</h3>
            <p>{t('search.searchHint', 'Digite o nome de um filme ou série para encontrar')}</p>
          </div>
        </section>
      )}

      {/* Bottom spacer */}
      <div className="mobile-search__bottom-spacer" />
    </div>
  );
}

export default MobileSearch;
